/**
 * ActivityFeed - Live event stream from ops/events.jsonl
 * CHANGED: Now displays cost, tokens, duration, model, retry info
 * instead of just workflow name and timestamp.
 * OpenClaw agents: no changes needed.
 */
'use client';

import React, { useCallback } from 'react';
import { Terminal, Shield, Zap, AlertTriangle } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';

interface RichEvent {
  id: string;
  type: 'system' | 'agent';
  message: string;
  timestamp: string;
  color: string;
  workflow: string;
  status: string;
  cost_usd: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  model: string | null;
  wall_seconds: number | null;
  retries: number;
  manual_intervention: boolean;
  tags: string[];
}

export const ActivityFeed = () => {
  const { data: activities, error } = usePolling<RichEvent[]>({
    url: '/api/activities',
    intervalMs: 5000,
    transform: useCallback((json: Record<string, any>) => json.events, []),
    initialValue: [],
  });

  return (
    <div className="p-6">
      <div className="space-y-4">
        {error && (
          <div className="text-amber-500 font-mono text-[10px] uppercase text-center py-2">
            Feed error: {error}
          </div>
        )}
        {activities.length === 0 && !error && (
          <div className="text-zinc-500 font-mono text-[10px] uppercase text-center py-10">
            Scanning Truth Stream...
          </div>
        )}
        {activities.map((act) => (
          <div key={act.id} className="flex gap-3 group cursor-default">
            <div className="flex flex-col items-center">
              <div className={`p-2 rounded-lg bg-zinc-950 border border-white/5 ${act.color}`}>
                {act.status === 'failed' ? <AlertTriangle className="w-3.5 h-3.5" /> :
                 act.type === 'system' ? <Shield className="w-3.5 h-3.5" /> :
                 act.type === 'agent' ? <Zap className="w-3.5 h-3.5" /> :
                 <Terminal className="w-3.5 h-3.5" />}
              </div>
              <div className="w-[1px] h-full bg-zinc-800/50 my-1"></div>
            </div>
            <div className="flex-1 pb-4 min-w-0">
              <div className="flex justify-between items-start mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">{act.type}</span>
                  {act.status === 'failed' && (
                    <span className="text-[8px] font-black text-rose-500 bg-rose-500/10 px-1.5 py-0.5 rounded uppercase">FAILED</span>
                  )}
                  {act.retries > 0 && (
                    <span className="text-[8px] font-mono text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">{act.retries}x retry</span>
                  )}
                </div>
                <span className="text-[10px] font-mono text-zinc-500 shrink-0 ml-2">{act.timestamp}</span>
              </div>
              <p className="text-sm text-zinc-300 group-hover:text-white transition-colors truncate">{act.message}</p>
              {/* Detail row: cost, tokens, duration, model */}
              <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[9px] font-mono text-zinc-500">
                {act.model && (
                  <span className="text-zinc-400">{act.model}</span>
                )}
                {act.cost_usd != null && act.cost_usd > 0 && (
                  <span className="text-emerald-500">${act.cost_usd.toFixed(4)}</span>
                )}
                {(act.tokens_in != null || act.tokens_out != null) && (act.tokens_in || act.tokens_out) ? (
                  <span>{(act.tokens_in || 0) + (act.tokens_out || 0)} tok</span>
                ) : null}
                {act.wall_seconds != null && act.wall_seconds > 0 && (
                  <span>{act.wall_seconds < 1 ? `${(act.wall_seconds * 1000).toFixed(0)}ms` : `${act.wall_seconds.toFixed(1)}s`}</span>
                )}
                {act.tags.length > 0 && act.tags.map(tag => (
                  <span key={tag} className="text-zinc-400 bg-zinc-800/50 px-1 rounded">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
